// Account protocol — the ONE definition shared by the app (src/backend/accountClient.ts,
// src/state/accountStore.ts) and the account-v2 edge function. Pure TypeScript: no imports, no runtime
// globals, so it runs in the browser, in Deno and in Node (same discipline as billingPolicy.ts).
//
// AUTHORITY: Owner decision list 2026-10-19 item 50 (docs/research/reports/待决事项清单-20261019.md) —
// 「注意验屋师的执照号码要跟着账号，免得账号借给别的验屋师用。」:
//   L-1  The licence number + the inspector's name are checked against New York's public list of licensed
//        home inspectors (data.ny.gov dataset r9fr-48vu, field `uid` = the licence number); they bind only
//        when the name matches and the licence is current.
//   L-2  Once bound, the inspector cannot change it; a different number goes through support, with proof.
//   L-3  One licence number binds to one account only.
//   L-4  The report's inspector name and licence number come from the account, never typed per inspection.
//   L-5  At most two devices at once (a phone and a computer); signing in on a third removes the one used
//        longest ago.
//   L-6  The licence's expiry is re-checked every month; an expired licence cannot produce a formal report
//        (Demo inspections are unaffected).
//   L-7  Signing in on a new device asks for a code sent to the account's e-mail.
export const ACCOUNT_PROTOCOL_VERSION = 1;
export const ACCOUNT_FUNCTION_PATH = '/functions/v1/account-v2';

export const NY_LICENSE_DATASET_URL = 'https://data.ny.gov/resource/r9fr-48vu.json';
export const MAX_ACTIVE_DEVICES = 2;
export const LICENSE_RECHECK_DAYS = 30;
// The e-mail code must have been entered this recently for a new device to be accepted (L-7).
export const DEVICE_CODE_PROOF_WINDOW_MS = 15 * 60 * 1000;

// ---------- the licence ----------

export interface BoundLicense {
  licenseNumber: string;
  // As the inspector typed them (checked against the list); the report prints `${firstName} ${lastName}`.
  firstName: string;
  lastName: string;
  // YYYY-MM-DD, the list's CURRENT EXPIRE DATE.
  expiresOn: string;
  boundAt: number;
  checkedAt: number;
}

export type LicenseState = 'unbound' | 'valid' | 'expired';

// 'YYYY-MM-DD' of `at` in New York — a New York licence expires at the end of its day there.
export function newYorkDate(at: number): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(at));
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function licenseStateOf(license: BoundLicense | null, at: number): LicenseState {
  if (!license) return 'unbound';
  return license.expiresOn >= newYorkDate(at) ? 'valid' : 'expired';
}

// "First Last"; a name written in Chinese characters reads family name first, without a space.
export function licenseHolderName(license: Pick<BoundLicense, 'firstName' | 'lastName'>): string {
  const first = license.firstName.trim();
  const last = license.lastName.trim();
  if (/[一-鿿]/.test(first) && /[一-鿿]/.test(last)) return `${last}${first}`;
  return `${first} ${last}`.trim();
}

// New York licence numbers for home inspectors are 11 digits (16000xxxxxx); spaces and dashes typed
// by a person are ignored.
export function normalizeLicenseNumber(raw: string): string | null {
  const digits = raw.replace(/[\s-]+/g, '');
  return /^\d{6,15}$/.test(digits) ? digits : null;
}

// Names are compared the way the list writes them: upper case, letters only. The last name must be the
// same; the first name must start with the same letter (the list often holds a short form — "TOM" for a
// Thomas — or the reverse). The number is the key and binds once; the name is the consistency check.
export function namePart(value: string): string {
  return value.normalize('NFKD').toUpperCase().replace(/[^A-Z]/g, '');
}

export function namesMatch(typed: { firstName: string; lastName: string }, listed: { firstName: string; lastName: string }): boolean {
  const typedLast = namePart(typed.lastName);
  const listedLast = namePart(listed.lastName);
  const typedFirst = namePart(typed.firstName);
  const listedFirst = namePart(listed.firstName);
  if (typedLast.length === 0 || typedFirst.length === 0) return false;
  if (typedLast !== listedLast) return false;
  return listedFirst.length > 0 && typedFirst[0] === listedFirst[0];
}

export interface ListedLicense {
  licenseNumber: string;
  firstName: string;
  lastName: string;
  expiresOn: string | null;
}

// One row of data.ny.gov r9fr-48vu → what the binding needs. Never throws.
export function listedLicenseFromRow(raw: unknown): ListedLicense | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const uid = typeof row.uid === 'string' ? row.uid : typeof row.uid === 'number' ? String(row.uid) : null;
  if (!uid) return null;
  const expire = typeof row.current_expire_date === 'string' ? row.current_expire_date.slice(0, 10) : null;
  return {
    licenseNumber: uid,
    firstName: typeof row.first_name === 'string' ? row.first_name : '',
    lastName: typeof row.last_name === 'string' ? row.last_name : '',
    expiresOn: expire && /^\d{4}-\d{2}-\d{2}$/.test(expire) ? expire : null
  };
}

export type BindOutcome = 'bound' | 'notListed' | 'nameMismatch' | 'expired' | 'takenByAnotherAccount' | 'accountHasAnotherLicense' | 'listUnavailable';

export const BIND_OUTCOMES: readonly BindOutcome[] = Object.freeze(['bound', 'notListed', 'nameMismatch', 'expired', 'takenByAnotherAccount', 'accountHasAnotherLicense', 'listUnavailable']);

// ---------- devices ----------

export type DevicePlatform = 'ios' | 'android' | 'web';

export interface AccountDevice {
  deviceId: string;
  platform: DevicePlatform;
  label: string;
  lastSeenAt: number;
  thisDevice: boolean;
}

// What the server says about the device asking.
//   active        registered and allowed
//   codeRequired  a new device: the e-mail code must be entered first (L-7)
//   revoked       removed because a newer device signed in (L-5) — sign out
export type DeviceState = 'active' | 'codeRequired' | 'revoked';

export function isDevicePlatform(value: unknown): value is DevicePlatform {
  return value === 'ios' || value === 'android' || value === 'web';
}

// A device id is a random id the app makes once per installation.
export function isDeviceId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{8,80}$/.test(value);
}

// ---------- request / response ----------

export interface AccountDeviceInfo {
  deviceId: string;
  platform: DevicePlatform;
  label: string;
}

export type AccountRequest =
  | { action: 'status'; protocolVersion: typeof ACCOUNT_PROTOCOL_VERSION; device: AccountDeviceInfo }
  | { action: 'bindLicense'; protocolVersion: typeof ACCOUNT_PROTOCOL_VERSION; device: AccountDeviceInfo; licenseNumber: string; firstName: string; lastName: string };

export interface AccountStatus {
  license: BoundLicense | null;
  licenseState: LicenseState;
  device: DeviceState;
  devices: AccountDevice[];
  serverTime: number;
}

export type AccountErrorCode = 'unauthenticated' | 'badRequest' | 'accountUnavailable' | 'internalError';

export const ACCOUNT_ERROR_STATUS: Readonly<Record<AccountErrorCode, number>> = Object.freeze({
  unauthenticated: 401,
  badRequest: 400,
  accountUnavailable: 503,
  internalError: 500
});

export type AccountResponse =
  | { ok: true; protocolVersion: typeof ACCOUNT_PROTOCOL_VERSION; status: AccountStatus; bind?: BindOutcome }
  | { ok: false; protocolVersion: typeof ACCOUNT_PROTOCOL_VERSION; code: AccountErrorCode; message: string };

export function accountError(code: AccountErrorCode, message: string): AccountResponse {
  return { ok: false, protocolVersion: ACCOUNT_PROTOCOL_VERSION, code, message };
}

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function str(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= max ? trimmed : null;
}

function checkDevice(raw: unknown): AccountDeviceInfo | null {
  if (!isRec(raw) || !isDeviceId(raw.deviceId) || !isDevicePlatform(raw.platform)) return null;
  return { deviceId: raw.deviceId, platform: raw.platform, label: (typeof raw.label === 'string' ? raw.label : '').trim().slice(0, 60) };
}

export function checkAccountRequest(raw: unknown): { ok: true; request: AccountRequest } | { ok: false; code: AccountErrorCode; message: string } {
  if (!isRec(raw)) return { ok: false, code: 'badRequest', message: 'request body is not an object' };
  if (raw.protocolVersion !== ACCOUNT_PROTOCOL_VERSION) return { ok: false, code: 'badRequest', message: `unsupported protocolVersion ${String(raw.protocolVersion)}` };
  const device = checkDevice(raw.device);
  if (!device) return { ok: false, code: 'badRequest', message: 'device missing or malformed' };
  if (raw.action === 'status') return { ok: true, request: { action: 'status', protocolVersion: ACCOUNT_PROTOCOL_VERSION, device } };
  if (raw.action !== 'bindLicense') return { ok: false, code: 'badRequest', message: 'action must be status or bindLicense' };
  const licenseNumber = typeof raw.licenseNumber === 'string' ? normalizeLicenseNumber(raw.licenseNumber) : null;
  const firstName = str(raw.firstName, 60);
  const lastName = str(raw.lastName, 60);
  if (!licenseNumber || !firstName || !lastName) return { ok: false, code: 'badRequest', message: 'licence number and name are required' };
  return { ok: true, request: { action: 'bindLicense', protocolVersion: ACCOUNT_PROTOCOL_VERSION, device, licenseNumber, firstName, lastName } };
}

export function checkBoundLicense(raw: unknown): BoundLicense | null {
  if (!isRec(raw)) return null;
  const licenseNumber = typeof raw.licenseNumber === 'string' ? normalizeLicenseNumber(raw.licenseNumber) : null;
  const firstName = str(raw.firstName, 60);
  const lastName = str(raw.lastName, 60);
  const expiresOn = typeof raw.expiresOn === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.expiresOn) ? raw.expiresOn : null;
  if (!licenseNumber || !firstName || !lastName || !expiresOn) return null;
  return { licenseNumber, firstName, lastName, expiresOn, boundAt: num(raw.boundAt), checkedAt: num(raw.checkedAt) };
}

export function checkAccountStatus(raw: unknown): AccountStatus | null {
  if (!isRec(raw)) return null;
  const license = raw.license === null ? null : checkBoundLicense(raw.license);
  if (raw.license !== null && license === null) return null;
  const device: DeviceState = raw.device === 'codeRequired' ? 'codeRequired' : raw.device === 'revoked' ? 'revoked' : raw.device === 'active' ? 'active' : 'active';
  const devices: AccountDevice[] = [];
  if (Array.isArray(raw.devices)) {
    for (const entry of raw.devices) {
      if (!isRec(entry) || !isDeviceId(entry.deviceId) || !isDevicePlatform(entry.platform)) continue;
      devices.push({ deviceId: entry.deviceId, platform: entry.platform, label: typeof entry.label === 'string' ? entry.label : '', lastSeenAt: num(entry.lastSeenAt), thisDevice: entry.thisDevice === true });
    }
  }
  const serverTime = num(raw.serverTime);
  return { license, licenseState: licenseStateOf(license, serverTime || Date.now()), device, devices, serverTime };
}

export function checkAccountResponse(raw: unknown): AccountResponse | null {
  if (!isRec(raw) || raw.protocolVersion !== ACCOUNT_PROTOCOL_VERSION) return null;
  if (raw.ok === false) {
    const code = typeof raw.code === 'string' && raw.code in ACCOUNT_ERROR_STATUS ? (raw.code as AccountErrorCode) : null;
    if (!code) return null;
    return { ok: false, protocolVersion: ACCOUNT_PROTOCOL_VERSION, code, message: typeof raw.message === 'string' ? raw.message : '' };
  }
  if (raw.ok !== true) return null;
  const status = checkAccountStatus(raw.status);
  if (!status) return null;
  const answer: AccountResponse = { ok: true, protocolVersion: ACCOUNT_PROTOCOL_VERSION, status };
  if (typeof raw.bind === 'string' && (BIND_OUTCOMES as readonly string[]).includes(raw.bind)) answer.bind = raw.bind as BindOutcome;
  return answer;
}
